import { useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  ClockIcon,
  MapPinIcon,
  ExclamationTriangleIcon,
  ShieldCheckIcon,
  DocumentArrowDownIcon,
  BuildingOffice2Icon,
  HeartIcon,
  ArrowPathIcon,
} from '@heroicons/react/24/outline';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import {
  requestOtp,
  exchangeOtp,
  fetchOpsPlan,
  downloadSharedAttachment,
  ShareUnavailableError,
  type PublicOpsPlanPayload,
} from '@/services/publicOpsPlan.service';

type Step = 'request' | 'otp' | 'view' | 'unavailable';

function fmtDatetime(value: string | null): string {
  if (!value) { return '—'; }
  try {
    return new Intl.DateTimeFormat(undefined, {
      dateStyle: 'medium',
      timeStyle: 'short',
    }).format(new Date(value));
  } catch {
    return value;
  }
}

function fmtExpiry(value: string): string {
  try {
    return new Intl.DateTimeFormat(undefined, {
      dateStyle: 'long',
      timeStyle: 'short',
    }).format(new Date(value));
  } catch {
    return value;
  }
}

export default function PublicOpsPlanView() {
  const { token = '' } = useParams<{ token: string }>();

  const [step, setStep] = useState<Step>('request');
  const [otp, setOtp] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [accessToken, setAccessToken] = useState('');
  const [payload, setPayload] = useState<PublicOpsPlanPayload | null>(null);

  function handleUnavailable(err: unknown): void {
    if (err instanceof ShareUnavailableError) {
      setStep('unavailable');
    } else {
      setError(err instanceof Error ? err.message : 'Something went wrong. Please try again.');
    }
  }

  function handleRequestOtp(): void {
    setBusy(true);
    setError(null);
    void (async () => {
      try {
        await requestOtp(token);
        setStep('otp');
      } catch (err) {
        handleUnavailable(err);
      } finally {
        setBusy(false);
      }
    })();
  }

  function handleResend(): void {
    setBusy(true);
    setError(null);
    void (async () => {
      try {
        await requestOtp(token);
        setError(null);
      } catch (err) {
        handleUnavailable(err);
      } finally {
        setBusy(false);
      }
    })();
  }

  function handleVerify(): void {
    setBusy(true);
    setError(null);
    void (async () => {
      try {
        const { accessToken: at } = await exchangeOtp(token, otp);
        const plan = await fetchOpsPlan(token, at);
        setAccessToken(at);
        setPayload(plan);
        setStep('view');
      } catch (err) {
        if (err instanceof ShareUnavailableError) {
          // By design: server returns SHARE_UNAVAILABLE for both wrong-code and dead-link.
          // Treat as retryable code error (do NOT navigate to unavailable).
          setError('That code was not accepted. Check the code, or request a new one.');
        } else {
          setError(err instanceof Error ? err.message : 'Something went wrong. Please try again.');
        }
      } finally {
        setBusy(false);
      }
    })();
  }

  function handleDownload(attachmentId: string, filename: string): void {
    void downloadSharedAttachment(token, accessToken, attachmentId, filename).catch((err: unknown) => {
      setError(err instanceof Error ? err.message : 'Download failed. Please try again.');
    });
  }

  // ── UNAVAILABLE ──────────────────────────────────────────────────────────
  if (step === 'unavailable') {
    return (
      <div className="min-h-screen bg-secondary-50 dark:bg-secondary-900 flex items-center justify-center px-4">
        <div className="max-w-md w-full">
          <Card>
            <CardContent className="p-8 sm:p-10 text-center">
              <ExclamationTriangleIcon className="w-16 h-16 text-amber-500 mx-auto mb-4" />
              <h1 className="text-2xl font-bold text-secondary-900 dark:text-white mb-2">
                This link is no longer available
              </h1>
              <p className="text-secondary-600 dark:text-secondary-400">
                It may have expired or been revoked. Contact the person who shared it with you.
              </p>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  // ── VIEW ─────────────────────────────────────────────────────────────────
  if (step === 'view' && payload) {
    const { activity, venue, attachments, share } = payload;

    return (
      <div className="min-h-screen bg-secondary-50 dark:bg-secondary-900 py-6 px-4 sm:px-6">
        {/* Classification banner */}
        <div className="max-w-3xl mx-auto mb-4">
          <div className="rounded-lg bg-amber-50 dark:bg-amber-900/20 border border-amber-300 dark:border-amber-700 px-4 py-3 text-center">
            <p className="text-xs font-semibold uppercase tracking-widest text-amber-800 dark:text-amber-300">
              CONFIDENTIAL — shared via VisionFirm for{' '}
              <span className="normal-case font-bold">{share.recipientName}</span>
              {' · '}expires {fmtExpiry(share.expiresAt)}
              {' · '}do not forward
            </p>
          </div>
        </div>

        <div className="max-w-3xl mx-auto space-y-4">
          {error && (
            <div className="p-3 bg-danger-50 dark:bg-danger-900/20 border border-danger-200 dark:border-danger-800 rounded-lg flex items-start gap-2">
              <ExclamationTriangleIcon className="w-5 h-5 text-danger-600 dark:text-danger-400 shrink-0 mt-0.5" />
              <p className="text-sm text-danger-800 dark:text-danger-200">{error}</p>
            </div>
          )}

          {/* Activity card */}
          <Card>
            <CardHeader>
              <CardTitle className="text-xl">{activity.title}</CardTitle>
              <p className="text-sm text-secondary-500 dark:text-secondary-400 mt-1">
                {activity.activityType} · {activity.status}
              </p>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="flex items-start gap-2">
                <ClockIcon className="w-4 h-4 text-secondary-400 shrink-0 mt-0.5" />
                <span className="text-sm text-secondary-700 dark:text-secondary-300">
                  {fmtDatetime(activity.startDatetime)} → {fmtDatetime(activity.endDatetime)}
                </span>
              </div>
              {activity.location && (
                <div className="flex items-start gap-2">
                  <MapPinIcon className="w-4 h-4 text-secondary-400 shrink-0 mt-0.5" />
                  <span className="text-sm text-secondary-700 dark:text-secondary-300">
                    {activity.location}
                  </span>
                </div>
              )}
              {activity.description && (
                <p className="text-sm text-secondary-700 dark:text-secondary-300 whitespace-pre-wrap">
                  {activity.description}
                </p>
              )}
            </CardContent>
          </Card>

          {/* Security requirements */}
          {activity.securityRequirements && (
            <Card>
              <CardHeader>
                <CardTitle className="text-base flex items-center gap-2">
                  <ShieldCheckIcon className="w-5 h-5 text-primary-500" />
                  Security Requirements
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-secondary-700 dark:text-secondary-300 whitespace-pre-wrap">
                  {activity.securityRequirements}
                </p>
              </CardContent>
            </Card>
          )}

          {/* Venue */}
          {venue && (
            <Card>
              <CardHeader>
                <CardTitle className="text-base flex items-center gap-2">
                  <BuildingOffice2Icon className="w-5 h-5 text-secondary-500" />
                  Venue
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                <p className="text-sm font-medium text-secondary-900 dark:text-white">{venue.name}</p>
                <p className="text-sm text-secondary-600 dark:text-secondary-400">{venue.address}</p>
                {venue.securityContactName && (
                  <p className="text-sm text-secondary-600 dark:text-secondary-400">
                    Security: {venue.securityContactName}
                    {venue.securityContactPhone && ` · ${venue.securityContactPhone}`}
                  </p>
                )}
              </CardContent>
            </Card>
          )}

          {/* Medical */}
          {activity.medical && (
            <Card>
              <CardHeader>
                <CardTitle className="text-base flex items-center gap-2">
                  <HeartIcon className="w-5 h-5 text-danger-500" />
                  Medical
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                {activity.medical.closestHospitalName && (
                  <div>
                    <p className="text-sm font-medium text-secondary-900 dark:text-white">
                      {activity.medical.closestHospitalName}
                    </p>
                    {activity.medical.closestHospitalAddress && (
                      <p className="text-sm text-secondary-600 dark:text-secondary-400">
                        {activity.medical.closestHospitalAddress}
                      </p>
                    )}
                    {activity.medical.closestHospitalPhone && (
                      <p className="text-sm text-secondary-600 dark:text-secondary-400">
                        Phone: {activity.medical.closestHospitalPhone}
                      </p>
                    )}
                    {activity.medical.closestHospitalTravelTime && (
                      <p className="text-sm text-secondary-600 dark:text-secondary-400">
                        Travel time: {activity.medical.closestHospitalTravelTime}
                      </p>
                    )}
                  </div>
                )}
                {activity.medical.alternativeMedicalFacilities && (
                  <div>
                    <p className="text-xs uppercase tracking-wide text-secondary-500 dark:text-secondary-400 font-medium mb-1">
                      Alternative Facilities
                    </p>
                    <p className="text-sm text-secondary-600 dark:text-secondary-400 whitespace-pre-wrap">
                      {activity.medical.alternativeMedicalFacilities}
                    </p>
                  </div>
                )}
                {activity.medical.onsiteMedicalSupport && (
                  <div>
                    <p className="text-xs uppercase tracking-wide text-secondary-500 dark:text-secondary-400 font-medium mb-1">
                      Onsite Medical Support
                    </p>
                    <p className="text-sm text-secondary-600 dark:text-secondary-400 whitespace-pre-wrap">
                      {activity.medical.onsiteMedicalSupport}
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {/* Evacuation / Risk */}
          {(activity.evacuationRoute ?? activity.riskAssessment) && (
            <Card>
              <CardHeader>
                <CardTitle className="text-base flex items-center gap-2">
                  <ExclamationTriangleIcon className="w-5 h-5 text-amber-500" />
                  Evacuation &amp; Risk
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {activity.evacuationRoute && (
                  <div>
                    <p className="text-xs uppercase tracking-wide text-secondary-500 dark:text-secondary-400 font-medium mb-1">
                      Evacuation Route
                    </p>
                    <p className="text-sm text-secondary-700 dark:text-secondary-300 whitespace-pre-wrap">
                      {activity.evacuationRoute}
                    </p>
                  </div>
                )}
                {activity.riskAssessment && (
                  <div>
                    <p className="text-xs uppercase tracking-wide text-secondary-500 dark:text-secondary-400 font-medium mb-1">
                      Risk Assessment
                    </p>
                    <p className="text-sm text-secondary-700 dark:text-secondary-300 whitespace-pre-wrap">
                      {activity.riskAssessment}
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {/* Attachments */}
          {attachments.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="text-base flex items-center gap-2">
                  <DocumentArrowDownIcon className="w-5 h-5 text-secondary-500" />
                  Attachments
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                {attachments.map((att) => (
                  <Button
                    key={att.id}
                    type="button"
                    variant="outline"
                    size="sm"
                    className="w-full justify-start gap-2"
                    onClick={() => { handleDownload(att.id, att.originalFilename); }}
                  >
                    <DocumentArrowDownIcon className="w-4 h-4 shrink-0" />
                    <span className="truncate">{att.originalFilename}</span>
                  </Button>
                ))}
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    );
  }

  // ── REQUEST / OTP ─────────────────────────────────────────────────────────
  return (
    <div className="min-h-screen bg-secondary-50 dark:bg-secondary-900 flex items-center justify-center px-4 py-12">
      <div className="max-w-md w-full">
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-12 h-12 rounded-xl bg-primary-600/10 mb-4">
            <ShieldCheckIcon className="w-6 h-6 text-primary-600" />
          </div>
          <h1 className="text-2xl font-bold text-secondary-900 dark:text-white">
            Secure Operations Plan
          </h1>
          <p className="text-secondary-600 dark:text-secondary-400 mt-2 text-sm">
            Shared via VisionFirm
          </p>
        </div>

        <Card>
          {step === 'request' && (
            <>
              <CardHeader>
                <CardTitle className="text-lg">Verify your identity</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <p className="text-sm text-secondary-600 dark:text-secondary-400">
                  To protect this plan, we&apos;ll email a 6-digit code to the address it was
                  shared with. Enter the code on the next screen to access the plan.
                </p>

                {error && (
                  <div className="p-3 bg-danger-50 dark:bg-danger-900/20 border border-danger-200 dark:border-danger-800 rounded-lg flex items-start gap-2">
                    <ExclamationTriangleIcon className="w-4 h-4 text-danger-600 dark:text-danger-400 shrink-0 mt-0.5" />
                    <p className="text-sm text-danger-800 dark:text-danger-200">{error}</p>
                  </div>
                )}

                <Button
                  type="button"
                  className="w-full"
                  disabled={busy}
                  loading={busy}
                  onClick={handleRequestOtp}
                >
                  Email me a verification code
                </Button>
              </CardContent>
            </>
          )}

          {step === 'otp' && (
            <>
              <CardHeader>
                <CardTitle className="text-lg">Enter your code</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <p className="text-sm text-secondary-600 dark:text-secondary-400">
                  We sent a 6-digit code to the email address on file. Enter it below.
                </p>

                {error && (
                  <div className="p-3 bg-danger-50 dark:bg-danger-900/20 border border-danger-200 dark:border-danger-800 rounded-lg flex items-start gap-2">
                    <ExclamationTriangleIcon className="w-4 h-4 text-danger-600 dark:text-danger-400 shrink-0 mt-0.5" />
                    <p className="text-sm text-danger-800 dark:text-danger-200">{error}</p>
                  </div>
                )}

                <Input
                  type="text"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  maxLength={6}
                  placeholder="000000"
                  value={otp}
                  onChange={(e) => {
                    setOtp(e.target.value.replace(/\D/g, ''));
                  }}
                  className="text-center text-xl tracking-widest font-mono"
                />

                <Button
                  type="button"
                  className="w-full"
                  disabled={busy || otp.length < 6}
                  loading={busy}
                  onClick={handleVerify}
                >
                  View ops plan
                </Button>

                <div className="text-center">
                  <button
                    type="button"
                    disabled={busy}
                    onClick={handleResend}
                    className="inline-flex items-center gap-1.5 text-sm text-primary-600 dark:text-primary-400 hover:underline disabled:opacity-50 disabled:pointer-events-none"
                  >
                    <ArrowPathIcon className="w-3.5 h-3.5" />
                    Resend code
                  </button>
                </div>
              </CardContent>
            </>
          )}
        </Card>
      </div>
    </div>
  );
}
